"use client";

import { useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { X, User } from "lucide-react";
import { updateCustomerProfile } from "../config/redux/slices/authAndAddressSlice";

export const COLORS = {
    kWhite: "#FFFFFF",
    inputBorder: "#E0E0E0",
    inputBackground: "#F0F0F0",
    primaryFontColor: "#000000",
    secondaryFontColor: "#666666",
    primaryColor: "#00489b",
    kBlack: "#000000",
};

export default function ProfileCompletionModal({ show, onClose, onProfileComplete }) {
    const [name, setName] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const dispatch = useDispatch();

    const handleNameChange = useCallback((e) => {
        const value = e.target.value;
        // Only allow letters and spaces
        const cleanValue = value.replace(/[^a-zA-Z\s]/g, '');
        setName(cleanValue);
    }, []);

    const handleSubmit = useCallback(async () => {
        const trimmedName = name.trim();

        if (!trimmedName) {
            toast.warn("Please enter your name");
            return;
        }

        if (trimmedName.length < 2) {
            toast.warn("Name must be at least 2 characters long");
            return;
        }

        try {
            setIsSubmitting(true);
            const result = await dispatch(updateCustomerProfile({ name: trimmedName, lastSource: "WEB",source:"WEB"})).unwrap();

            if (result?.value) {
                toast.success("Profile updated successfully!");
                // Close modal first, then call onProfileComplete
                handleClose();
                // Small delay to ensure modal is closed before calling callback
                setTimeout(() => {
                    onProfileComplete(result.value);
                }, 100);
            } else {
                toast.error("Failed to update profile");
            }
        } catch (error) {
            console.error("Profile update error:", error);
            toast.error("Failed to update profile. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    }, [name, dispatch, onProfileComplete]);

    const handleClose = () => {
        setName("");
        setIsSubmitting(false);
        onClose();
    };

    if (!show) return null;

    return (
        <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-300"
        >
            <div
                className="bg-white rounded-[12px] shadow-2xl w-full max-w-md p-6 sm:p-8 relative max-h-[90vh] overflow-y-auto animate-in slide-in-from-bottom-4 duration-300"
            >

                <p className="text-sm mb-6" style={{ color: COLORS.secondaryFontColor }}>
                    Please enter your full name to complete your profile setup. This step is required to continue.
                </p>

                <div className="flex flex-col items-center mb-6">
                </div>

                <input
                    type="text"
                    placeholder="Enter your full name"
                    value={name}
                    onChange={handleNameChange}
                    disabled={isSubmitting}
                    className="w-full p-4 mb-6 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition disabled:opacity-50 text-lg"
                    style={{
                        borderColor: COLORS.inputBorder,
                        backgroundColor: COLORS.inputBackground,
                        color: COLORS.primaryFontColor,
                    }}
                    autoFocus
                    maxLength={50}
                />

                <button
                    onClick={handleSubmit}
                    disabled={isSubmitting || !name.trim()}
                    className="w-full py-4 bg-gradient-to-r bg-[#00489b] text-white rounded-[12px] font-semibold hover:from-blue-600 hover:to-indigo-700 transition disabled:opacity-50 text-lg"
                >
                    {isSubmitting ? "Updating..." : "Complete Profile"}
                </button>
            </div>
        </div>
    );
}
